import { useCallback, useEffect, useState } from 'react'
import { publicConfig } from 'config'
import {
  memberNotificationLinks,
  type MemberNotificationLinks,
} from 'lib/community/hub-notifications'
import type { ownHubHandle } from 'lib/community/settings'
import { sharedMe } from 'lib/owner-notes/api'
import type { OwnerMeResponse } from 'lib/dealhunt/types'
import { hasDeviceMarker } from 'lib/owner-notes/declared'

/** The state of this account's `/me` read (see `useMemberNotificationLinks`'s `read`). */
export type MeRead = 'idle' | 'loading' | 'settled'

/** What the avatar menu's head and hub entries read off `/me` — the remembered slice. */
export type MeHead = NonNullable<Parameters<typeof ownHubHandle>[0]>

const KEY = 'sg_me_head'

function headOf(me: OwnerMeResponse): MeHead {
  return {
    profile: { handle: me.profile.handle, claimed: me.profile.claimed },
    shelf: me.shelf.map(item => ({ note: { status: item.note.status } })),
    community: me.community,
    settings: me.settings ? { profile_public: me.settings.profile_public } : null,
  }
}

/** The head remembered in this tab for `account` (another account's is ignored), or null. */
function rememberedHead(account: unknown): MeHead | null {
  if (!account || typeof window === 'undefined') {
    return null
  }
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(KEY) ?? 'null') as {
      id?: unknown
      head?: MeHead
    } | null
    return stored?.id === account && stored.head?.profile?.handle ? stored.head : null
  } catch {
    return null
  }
}

function rememberHead(account: unknown, head: MeHead | null): void {
  if (!account || typeof window === 'undefined') {
    return
  }
  try {
    if (head) {
      window.sessionStorage.setItem(KEY, JSON.stringify({ id: account, head }))
    } else {
      window.sessionStorage.removeItem(KEY)
    }
  } catch {
    // storage blocked: the head still shows for this page once /me answers
  }
}

/**
 * The bell's links to the member's Notifications tab — and `/me` itself, for the avatar menu's
 * identity block and hub entries (Member Hub 2a) — resolved LAZILY: `resolve` is called when the
 * account menu opens (HeaderAuth `onMenuOpen`), never on page view — the header is on every page
 * and a `/me` per view is not worth a link. No owner device on this browser = no member page to
 * point at: nothing is asked and the bell keeps its inline settings.
 *
 * The head does not wait for that read from the second page on: the slice the menu shows
 * (handle, tier, points, progress — `MeHead`) is remembered per tab and account in
 * sessionStorage from the last answer, so the menu opens on the You card at once and the open's
 * re-read (`sharedMe`, deduped) refreshes it in place. No extra request: a page view still never
 * reads `/me` (Constraint a8b375cd); a page without an owner device forgets the head.
 */
export function useMemberNotificationLinks(account: unknown = null): {
  links: MemberNotificationLinks | undefined
  me: MeHead | null
  resolve: () => void
  /** Hover/focus/touch on the avatar: resolve once ahead of the click, so the menu head is ready. */
  prefetch: () => void
  /** This account's `/me` read: `idle` before any, `loading` in flight, `settled` once it answered
   *  OR failed (or there was nothing to ask — no owner device). The menu head holds its line for a
   *  remembered member only until `settled` (`memberHeadPending`). */
  read: MeRead
} {
  const [links, setLinks] = useState<MemberNotificationLinks | undefined>(undefined)
  const [me, setMe] = useState<MeHead | null>(null)
  const [read, setRead] = useState<MeRead>('idle')
  const trailingSlash = publicConfig('useTrailingSlash')
  // `account` is the signed-in reader's identity (commentHub user id; null signed out): a
  // sign-out or another account signing in (no reload) must not keep the previous member's
  // links or head. The new account starts from the head this tab remembers for it, if any.
  // A re-open by the same reader keeps the last answer while `/me` is re-read — no flash of
  // the slim row before the (usually cached) answer.
  useEffect(() => {
    const remembered = rememberedHead(account)
    setMe(remembered)
    setRead('idle')
    setLinks(
      remembered ? memberNotificationLinks(remembered, trailingSlash) ?? undefined : undefined
    )
  }, [account, trailingSlash])
  const resolve = useCallback(() => {
    if (!hasDeviceMarker()) {
      setLinks(undefined)
      setMe(null)
      rememberHead(account, null)
      setRead('settled')
      return
    }
    setRead('loading')
    sharedMe().then(
      answer => {
        const head = headOf(answer)
        setMe(head)
        setLinks(memberNotificationLinks(answer, trailingSlash) ?? undefined)
        rememberHead(account, head)
        setRead('settled')
      },
      // A failed read settles too: the head shows the reader as a non-member (name + email)
      // rather than hold an empty line forever.
      () => setRead('settled')
    )
  }, [account, trailingSlash])
  // One prefetch per page view (and per account): intent fires on every hover, the click still
  // re-reads (sharedMe dedupes and caches).
  const [prefetched, setPrefetched] = useState(false)
  useEffect(() => setPrefetched(false), [account])
  const prefetch = useCallback(() => {
    if (prefetched) {
      return
    }
    setPrefetched(true)
    resolve()
  }, [prefetched, resolve])
  return { links, me, resolve, prefetch, read }
}
